import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as Globe } from "assets/svgs/globe.svg";
import { ReactComponent as LanguageIcon } from "assets/svgs/language.svg";
import { CURRENT_COUNTRY } from "utils/constants";
import { useSelector } from "react-redux";
import Modal from "components/modal";
import Image from "components/image";
import CommonDropdown from "components/common-dropdown";
import useToggle from "hooks/useToggle";
import { useGlobalContext } from "context/global";
import { Link } from "gatsby";
import clsx from "clsx/lite";

const LanguagePicker = ({ path }) => {
	const { language, languages, countries, translation } = useGlobalContext();
	const { isClient } = useSelector(state => state.global);
	const [config, setConfig] = useState({
		selectedLanguage: language,
		selectedCountry: countries.length > 0 ? countries?.find(c => c.id === CURRENT_COUNTRY) : {},
	});
	const [isOpen, toggle] = useToggle();

	useEffect(() => {
		window._elev?.setLanguage(language);
	}, [language]);

	const getPreference = () => {
		// Check if the selected country is the current country, if so, just change the language
		if (config.selectedCountry.id === CURRENT_COUNTRY) {
			return {
				path: getUpdatedPath(config.selectedLanguage), internal: true
			};
		}

		// If the selected country is not the current country, return the path to the selected country
		return {
			path: `${config.selectedCountry.url}${config.selectedLanguage === "default" ? "" : "/en"}`, internal: false,
		};
	};

	// Get the updated path based on the selected language
	const getUpdatedPath = useCallback((lang) => {
		const isEnPath = path?.includes("/en");
		const queryString = isClient ? window.location.search : "";

		if (isEnPath && lang === "default")
			return `${path.replace("/en", path === "/en" ? "/" : "")}${queryString}`;
		else if (!isEnPath && lang === "en")
			return `/en${path === "/" ? "" : path}${queryString}`;
		else return path;

	}, [path]);

	const handleCountryChange = (e) => {
		const country = countries.find((c) => c._key === e.target.value);
		setConfig(prev => ({
			selectedCountry: country,
			selectedLanguage: country._key !== config.selectedCountry._key ? "default" : prev.selectedLanguage,
		}));
	};

	const handleLanguageChange = (e) => setConfig(prev => ({
		...prev,
		selectedLanguage: e.target.value,
	}));

	const preference = isOpen ? getPreference() : {};

	return (
		<div className="inline-flex items-center gap-2.5 flex-col">
			{languages?.map((lang) => (
				<button
					role="button"
					aria-label="choose language"
					className={clsx("w-6 cursor-pointer rounded-full", lang.id === language ? "block " : "hidden")}
					key={lang.id} onClick={toggle}
				>
					<Image className="language-picker-flag" image={lang.flag} />
				</button>
			))}
			{countries?.length > 0 && isOpen && (
				<Modal className="w-90 mx-auto md:max-w-md" isOpen={isOpen} headerStyle="!pb-1 !pr-4" close={toggle}>
					<div className="w-full flex justify-center flex-col">
						<p className="text-secondary text-small mx-auto text-center">{translation.other?.choose_location_language}</p>
						<CommonDropdown
							className="mt-4"
							label={translation.other?.choose_country}
							icon={Globe}
							selectedValue={config.selectedCountry?._key}
							displayName={config.selectedCountry?.name}
							handleChange={handleCountryChange}
							options={countries}
							useKeyAsOptionValue={true}
							aria-label="Select your country"
						/>
						<CommonDropdown
							className="mt-4"
							label={translation.other?.choose_language}
							icon={LanguageIcon}
							displayName={config.selectedCountry?.languages?.find((l) => l.id === config.selectedLanguage)?.name}
							selectedValue={config.selectedLanguage}
							handleChange={handleLanguageChange}
							options={config.selectedCountry?.languages}
							aria-label="Select your language"
						/>
						{preference.internal ?
							<Link to={preference.path} state={{ preserveScroll: true }}
								onClick={() => preference.path === path && toggle()}
								className={clsx("btn-primary px-2.5 mt-4 mb-6 mx-auto w-4/5")}>
								{translation.other?.update_preferences}
							</Link>
							:
							<a href={preference.path}
								className="btn-primary px-2.5 mt-4 mb-6 mx-auto w-4/5">
								{translation.other?.update_preferences}
							</a>
						}
					</div>
				</Modal>
			)}
		</div>
	);
};

export default LanguagePicker;